// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — PA Grants Applicant Detail (Screen 3)
// Organization detail view with sidebar, stats, and tabbed content
// Full CRUD: projects, damage sites, org edit, documents
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Paper, Text, Group, Stack, Badge, Button, SimpleGrid, Table, Tabs,
  Loader, Center, Title, ActionIcon, Modal, TextInput, Select, Textarea,
  Checkbox, Popover, Progress, ThemeIcon,
} from '@mantine/core';
import {
  IconEdit, IconPlus, IconSearch, IconUpload, IconDownload, IconTrash,
  IconArrowLeft, IconFile, IconFileTypePdf, IconFileSpreadsheet,
  IconClock, IconCircleCheck, IconCircle, IconGitMerge,
  IconListCheck, IconInfoCircle, IconExternalLink,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useWorkflowStore } from '../../store/workflowStore';
import { useAuthStore } from '../../store/authStore';
import { useMyPAOrgIds } from '../../hooks/useMyPAOrgData';
import { useRoleStore } from '../../store/roleStore';
import { TABLES, FIELDS, createRecord, updateRecord, WORKER_URL } from '../../lib/api-switch';
import { DAMAGE_CATEGORY_COLORS, DAMAGE_CATEGORY_LABELS } from '../../types/pagrants';
import type { DamageCategory, PAProject, PADamageItem, PAOrganization } from '../../types/pagrants';
import type { WorkflowStepDef } from '../../types/workflow';
import { STEP_TYPE_LABELS, STEP_TYPE_COLORS } from '../../types/workflow';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

// ── Helpers ──────────────────────────────────────────────────

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const ROLE_COLORS: Record<string, string> = {
  grantee: 'blue', subgrantee: 'violet', applicant: 'teal',
};

const PHASE_COLORS: Record<string, string> = {
  formulation: 'blue', review: 'yellow', obligation: 'green',
  work_in_progress: 'orange', closeout: 'violet', appeal: 'red',
};

const PROJECT_STATUS_COLORS: Record<string, string> = {
  draft: 'gray', in_review: 'yellow', approved: 'green', obligated: 'teal',
  in_progress: 'cyan', completed: 'green', closed: 'gray', appealed: 'red',
};

const CATEGORY_OPTIONS = Object.entries(DAMAGE_CATEGORY_LABELS).map(([value, label]) => ({ value, label }));

const PROJECT_SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'large', label: 'Large' },
];

const PROJECT_PHASE_OPTIONS = [
  { value: 'formulation', label: 'Formulation' },
  { value: 'review', label: 'Review' },
  { value: 'obligation', label: 'Obligation' },
  { value: 'work_in_progress', label: 'Work in Progress' },
  { value: 'closeout', label: 'Closeout' },
  { value: 'appeal', label: 'Appeal' },
];

const PROJECT_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'obligated', label: 'Obligated' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
];

const DAMAGE_SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'major', label: 'Major' },
  { value: 'destroyed', label: 'Destroyed' },
];

const DAMAGE_STATUS_OPTIONS = [
  { value: 'identified', label: 'Identified' },
  { value: 'inspected', label: 'Inspected' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'eligible', label: 'Eligible' },
  { value: 'ineligible', label: 'Ineligible' },
];

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const WORK_TYPE_OPTIONS = [
  { value: 'emergency_work', label: 'Emergency Work (Cat A/B)' },
  { value: 'permanent_repair', label: 'Permanent Repair' },
  { value: 'permanent_replace', label: 'Permanent Replacement' },
  { value: 'improved_project', label: 'Improved Project (406)' },
  { value: 'alternate_project', label: 'Alternate Project (428)' },
  { value: 'debris_removal', label: 'Debris Removal' },
  { value: 'emergency_protective', label: 'Emergency Protective Measures' },
];

// ── Address autocomplete via HERE API ────────────────────────
const WORKER = WORKER_URL;
const WORKER_HEADERS = { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' };

interface HereSuggestion {
  title: string;
  address?: { label?: string; street?: string; city?: string; county?: string; state?: string; postalCode?: string };
  position?: { lat: number; lng: number };
}

// ── Currency formatting ──────────────────────────────────────
function formatCurrency(raw: string): string {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (!cleaned) return '';
  const parts = cleaned.split('.');
  const whole = parts[0].replace(/^0+(?=\d)/, '');
  const formatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (parts.length > 1) return `$${formatted}.${parts[1].slice(0, 2)}`;
  return formatted ? `$${formatted}` : '';
}
function stripCurrency(formatted: string): string {
  return formatted.replace(/[$,]/g, '');
}

/** Generate next PW number from existing projects */
function nextProjectNumber(projects: PAProject[]): string {
  let max = 0;
  for (const p of projects) {
    const m = p.projectNumber.match(/PW-?(\d+)/i);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `PW-${String(max + 1).padStart(3, '0')}`;
}

const ORG_TYPE_OPTIONS = [
  { value: 'state', label: 'State' },
  { value: 'county', label: 'County' },
  { value: 'city', label: 'City' },
  { value: 'town', label: 'Town' },
  { value: 'township', label: 'Township' },
  { value: 'district', label: 'District' },
  { value: 'tribal', label: 'Tribal' },
  { value: 'pnp', label: 'Private Non-Profit' },
];

const ORG_ROLE_OPTIONS = [
  { value: 'grantee', label: 'Grantee' },
  { value: 'subgrantee', label: 'Subgrantee' },
  { value: 'applicant', label: 'Applicant' },
];

const ORG_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
];

const INDIRECT_COST_RATE_TYPE_OPTIONS = [
  { value: 'negotiated', label: 'Negotiated' },
  { value: 'de_minimis', label: 'De Minimis (10%)' },
  { value: 'none', label: 'None' },
];

function fileIcon(mimeType: string) {
  if (mimeType.includes('pdf')) return <IconFileTypePdf size={18} color="#DC2626" />;
  if (mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return <IconFileSpreadsheet size={18} color="#059669" />;
  return <IconFile size={18} color="#6B7280" />;
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '8px 0',
      borderBottom: '1px solid var(--mantine-color-gray-2)', fontSize: 13,
    }}>
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm" fw={500}>{value || '---'}</Text>
    </div>
  );
}

// ── Default form states ──────────────────────────────────────

const defaultProjectForm: Partial<PAProject> = {
  projectNumber: '', projectTitle: '', category: '', projectSize: '',
  workType: '', projectAddress: '', projectCity: '', projectCounty: '',
  projectState: '', projectZip: '', damageDescription: '', scopeOfWork: '',
  estimatedCost: '', federalSharePct: '75', assignedSpecialist: '',
  phase: 'formulation', status: 'draft', notes: '',
};

const defaultDamageForm: Partial<PADamageItem> = {
  facilityName: '', facilityType: '', category: '', damageSeverity: '',
  siteAddress: '', siteCity: '', siteCounty: '', siteState: '', siteZip: '',
  damageDescription: '', damageCause: '', estimatedCost: '',
  preDisasterCondition: '', preDisasterUse: '',
  siteInspected: '', siteInspectionDate: '', projectId: '',
  status: 'identified', notes: '',
};

function buildOrgForm(org: PAOrganization): Partial<PAOrganization> {
  return {
    orgName: org.orgName, orgType: org.orgType, orgRole: org.orgRole,
    fipsCode: org.fipsCode, femaApplicantId: org.femaApplicantId,
    ueiNumber: org.ueiNumber, samRegistered: org.samRegistered,
    samExpirationDate: org.samExpirationDate,
    congressionalDistrict: org.congressionalDistrict,
    physicalAddress: org.physicalAddress, physicalCity: org.physicalCity,
    physicalState: org.physicalState, physicalZip: org.physicalZip,
    physicalCounty: org.physicalCounty,
    mailingAddress: org.mailingAddress, mailingCity: org.mailingCity,
    mailingState: org.mailingState, mailingZip: org.mailingZip,
    primaryContact: org.primaryContact, primaryTitle: org.primaryTitle,
    primaryEmail: org.primaryEmail, primaryPhone: org.primaryPhone,
    altContact: org.altContact, altTitle: org.altTitle,
    altEmail: org.altEmail, altPhone: org.altPhone,
    contactEmails: org.contactEmails,
    procurementPolicyOnFile: org.procurementPolicyOnFile,
    forceAccountPolicyOnFile: org.forceAccountPolicyOnFile,
    singleAuditRequired: org.singleAuditRequired,
    singleAuditCurrent: org.singleAuditCurrent,
    indirectCostRate: org.indirectCostRate,
    indirectCostRateType: org.indirectCostRateType,
    status: org.status, notes: org.notes,
  };
}

// ── Derive mime/doc type from file extension ─────────────────

function deriveDocType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    pdf: 'PDF', doc: 'Document', docx: 'Document', xlsx: 'Spreadsheet',
    xls: 'Spreadsheet', csv: 'Spreadsheet', pptx: 'Presentation',
    ppt: 'Presentation', png: 'Image', jpg: 'Image', jpeg: 'Image',
    gif: 'Image', zip: 'Archive', rar: 'Archive',
  };
  return map[ext] || 'Other';
}

// ── Component ────────────────────────────────────────────────

export default function ApplicantDetail() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const routeBase = pathname.includes('/pagrants/hmgp') ? '/pagrants/hmgp' : '/pagrants/pa';
  const token = useAuthStore(s => s.token);
  const myOrgIds = useMyPAOrgIds();
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicantRole = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'].includes(effectiveRole?.roleSlug ?? '');

  const {
    organizations, projects, damageItems, documents, disasters, timelineEvents,
    loadingOrgs, loadingProjects, loadingDamage, loadingDocs,
    fetchAll, createProject, updateProject, deleteProject,
    createDamageItem, updateDamageItem, deleteDamageItem, updateOrg,
    createTimelineEvent, updateTimelineEvent, deleteTimelineEvent,
  } = usePAGrantsStore();

  useEffect(() => {
    if (organizations.length === 0) fetchAll();
  }, [organizations.length, fetchAll]);

  const org = useMemo(
    () => organizations.find(o => o.Rid === Number(orgId)),
    [organizations, orgId],
  );

  const orgProjects = useMemo(
    () => projects.filter(p => p.organizationId === String(orgId)),
    [projects, orgId],
  );

  const orgDamageItems = useMemo(
    () => damageItems.filter(d => d.organizationId === String(orgId)),
    [damageItems, orgId],
  );

  const orgDocuments = useMemo(
    () => documents.filter(d => d.organizationId === String(orgId)),
    [documents, orgId],
  );

  const activeDisaster = useMemo(
    () => disasters.find(d => d.status === 'active'),
    [disasters],
  );

  // ── Stats ────────────────────────────────────────────────
  const projectCount = orgProjects.length;
  const obligatedTotal = orgProjects.reduce((s, p) => s + (parseFloat(p.obligatedAmount) || 0), 0);
  const estimatedTotal = orgProjects.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0);
  const pendingReview = orgProjects.filter(p => p.status === 'in_review').length;

  // ── Grouped documents by type ────────────────────────────
  const docsByType = useMemo(() => {
    const groups: Record<string, typeof orgDocuments> = {};
    for (const doc of orgDocuments) {
      const tag = doc.documentType || 'Other';
      if (!groups[tag]) groups[tag] = [];
      groups[tag].push(doc);
    }
    return groups;
  }, [orgDocuments]);

  // ════════════════════════════════════════════════════════════
  // MODAL STATE
  // ════════════════════════════════════════════════════════════

  // ── Project modal ──
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<PAProject | null>(null);
  const [projectForm, setProjectForm] = useState<Partial<PAProject>>(defaultProjectForm);
  const [projectSaving, setProjectSaving] = useState(false);
  const [projectTab, setProjectTab] = useState<string | null>('general');

  // ── Damage site modal ──
  const [damageModalOpen, setDamageModalOpen] = useState(false);
  const [editingDamage, setEditingDamage] = useState<PADamageItem | null>(null);
  const [damageForm, setDamageForm] = useState<Partial<PADamageItem>>(defaultDamageForm);
  const [damageSaving, setDamageSaving] = useState(false);

  // ── Edit org modal ──
  const [editOrgModalOpen, setEditOrgModalOpen] = useState(false);
  const [orgForm, setOrgForm] = useState<Partial<PAOrganization>>({});
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgTab, setOrgTab] = useState<string | null>('basic');
  const [mailingSameAsPhysical, setMailingSameAsPhysical] = useState(false);

  // ── Delete confirmation ──
  const [deleteProjectTarget, setDeleteProjectTarget] = useState<PAProject | null>(null);
  const [deleteDamageTarget, setDeleteDamageTarget] = useState<PADamageItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Document upload ──
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Task modal ──
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ description: '', dueDate: '', projectId: '', status: 'pending' });
  const [taskSaving, setTaskSaving] = useState(false);

  // ── Address autocomplete for project modal ──
  const [addrSuggestions, setAddrSuggestions] = useState<HereSuggestion[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAddrSuggestions = useCallback((query: string) => {
    if (addrTimerRef.current) clearTimeout(addrTimerRef.current);
    if (query.length < 3) { setAddrSuggestions([]); setAddrOpen(false); return; }
    addrTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${WORKER}/api/here/autosuggest?q=${encodeURIComponent(query)}&at=33.749,-84.388&limit=5`,
          { headers: WORKER_HEADERS },
        );
        const data = await res.json() as { items?: HereSuggestion[] };
        const items = (data.items ?? []).filter(i => i.address);
        setAddrSuggestions(items);
        setAddrOpen(items.length > 0);
      } catch { /* ignore */ }
    }, 300);
  }, []);

  const pickProjectAddress = (s: HereSuggestion) => {
    setProjectForm(prev => ({
      ...prev,
      projectAddress: s.address?.street || s.address?.label || s.title,
      projectCity: s.address?.city || '',
      projectCounty: s.address?.county || '',
      projectState: s.address?.state || '',
      projectZip: s.address?.postalCode || '',
    }));
    setAddrOpen(false);
    setAddrSuggestions([]);
  };

  // ── Workflow data for projects ──
  const { instances, steps: wfSteps, templates: wfTemplates, loadInstances, loadSteps, loadTemplates: loadWfTemplates } = useWorkflowStore();

  useEffect(() => {
    loadInstances();
    loadSteps();
    loadWfTemplates();
  }, [loadInstances, loadSteps, loadWfTemplates]);

  // Map projectRid -> active workflow instance + parsed step defs
  const projectWorkflows = useMemo(() => {
    const map = new Map<string, {
      instance: typeof instances[0];
      activeStep: typeof wfSteps[0] | null;
      stepDefs: WorkflowStepDef[];
      allSteps: typeof wfSteps;
    }>();
    for (const inst of instances) {
      if (inst.sourceModule !== 'pagrants' || inst.status !== 'active') continue;
      const instSteps = wfSteps.filter(s => s.instanceId === String(inst.Rid));
      const activeStep = instSteps.find(s => s.status === 'in_progress') || null;
      let stepDefs: WorkflowStepDef[] = [];
      const tmpl = wfTemplates.find(t => String(t.Rid) === inst.templateId);
      try { if (tmpl?.stepsJson) stepDefs = JSON.parse(tmpl.stepsJson); } catch { /* */ }
      map.set(inst.sourceRecordId, { instance: inst, activeStep, stepDefs, allSteps: instSteps });
    }
    return map;
  }, [instances, wfSteps, wfTemplates]);

  // ── Tasks derived from timelineEvents ──
  const orgTasks = useMemo(
    () => timelineEvents
      .filter(t => t.organizationId === String(orgId) && (t.eventType === 'task' || t.eventType === 'deadline'))
      .sort((a, b) => (a.dueDate || '9').localeCompare(b.dueDate || '9')),
    [timelineEvents, orgId],
  );

  const today = new Date().toISOString().split('T')[0];

  // ════════════════════════════════════════════════════════════
  // MODAL HANDLERS
  // ════════════════════════════════════════════════════════════

  // ── Project handlers ──
  function openCreateProject() {
    setEditingProject(null);
    setProjectForm({
      ...defaultProjectForm,
      organizationId: String(orgId),
      disasterId: activeDisaster ? String(activeDisaster.Rid) : '',
      projectNumber: nextProjectNumber(projects),
    });
    setProjectTab('general');
    setProjectModalOpen(true);
  }

  function openEditProject(p: PAProject) {
    setEditingProject(p);
    setProjectForm({
      projectNumber: p.projectNumber, projectTitle: p.projectTitle, category: p.category,
      projectSize: p.projectSize, workType: p.workType,
      projectAddress: p.projectAddress, projectCity: p.projectCity, projectCounty: p.projectCounty,
      projectState: p.projectState, projectZip: p.projectZip,
      damageDescription: p.damageDescription, scopeOfWork: p.scopeOfWork,
      estimatedCost: p.estimatedCost, federalSharePct: p.federalSharePct || '75',
      assignedSpecialist: p.assignedSpecialist,
      phase: p.phase, status: p.status, notes: p.notes,
      organizationId: p.organizationId, disasterId: p.disasterId,
    });
    setProjectTab('general');
    setProjectModalOpen(true);
  }

  async function handleSaveProject() {
    setProjectSaving(true);
    try {
      if (editingProject) {
        await updateProject(editingProject.Rid, projectForm);
        notifications.show({ title: 'Project updated', message: projectForm.projectTitle || 'Saved', color: 'green' });
      } else {
        const newRid = await createProject(projectForm);
        notifications.show({ title: 'Project created', message: projectForm.projectTitle || 'Saved', color: 'green' });

        // Auto-start PA workflow if a matching template exists
        try {
          const { templates, loadTemplates, startWorkflow } = useWorkflowStore.getState();
          const { user } = useAuthStore.getState();
          if (user) {
            if (templates.length === 0) await loadTemplates();
            const allT = useWorkflowStore.getState().templates;
            const paT = allT.find(t => (t.active === 'yes' || t.active === 'Yes') && t.module === 'pagrants');
            if (paT) {
              const title = projectForm.projectTitle ?? '';
              const num = projectForm.projectNumber ?? '';
              const label = num ? `${num} — ${title}` : title;
              await startWorkflow(String(paT.Rid), 'pagrants', TABLES.paProjects, String(newRid), label, user.email);
              notifications.show({
                title: 'Workflow Started',
                message: `"${paT.name}" workflow started for ${label}`,
                color: 'violet',
                icon: <IconGitMerge size={16} />,
              });
            }
          }
        } catch { /* Don't fail project creation */ }
      }
      setProjectModalOpen(false);
    } catch (e: unknown) {
      notifications.show({ title: 'Error', message: e instanceof Error ? e.message : 'Failed to save project', color: 'red' });
    } finally {
      setProjectSaving(false);
    }
  }

  // ── Damage site handlers ──
  function openCreateDamage() {
    setEditingDamage(null);
    setDamageForm({ ...defaultDamageForm, organizationId: String(orgId), disasterId: activeDisaster ? String(activeDisaster.Rid) : '' });
    setDamageModalOpen(true);
  }

  function openEditDamage(d: PADamageItem) {
    setEditingDamage(d);
    setDamageForm({
      facilityName: d.facilityName, facilityType: d.facilityType, category: d.category,
      damageSeverity: d.damageSeverity,
      siteAddress: d.siteAddress, siteCity: d.siteCity, siteCounty: d.siteCounty,
      siteState: d.siteState, siteZip: d.siteZip,
      damageDescription: d.damageDescription, damageCause: d.damageCause,
      estimatedCost: d.estimatedCost,
      preDisasterCondition: d.preDisasterCondition, preDisasterUse: d.preDisasterUse,
      siteInspected: d.siteInspected, siteInspectionDate: d.siteInspectionDate,
      projectId: d.projectId, status: d.status, notes: d.notes,
      organizationId: d.organizationId, disasterId: d.disasterId,
    });
    setDamageModalOpen(true);
  }

  async function handleSaveDamage() {
    setDamageSaving(true);
    try {
      if (editingDamage) {
        await updateDamageItem(editingDamage.Rid, damageForm);
        notifications.show({ title: 'Damage site updated', message: damageForm.facilityName || 'Saved', color: 'green' });
      } else {
        await createDamageItem(damageForm);
        notifications.show({ title: 'Damage site created', message: damageForm.facilityName || 'Saved', color: 'green' });
      }
      setDamageModalOpen(false);
    } catch (e: unknown) {
      notifications.show({ title: 'Error', message: e instanceof Error ? e.message : 'Failed to save damage site', color: 'red' });
    } finally {
      setDamageSaving(false);
    }
  }

  // ── Org edit handlers ──
  function openEditOrg() {
    if (!org) return;
    setOrgForm(buildOrgForm(org));
    setMailingSameAsPhysical(false);
    setOrgTab('basic');
    setEditOrgModalOpen(true);
  }

  async function handleSaveOrg() {
    if (!org) return;
    setOrgSaving(true);
    try {
      const data = { ...orgForm };
      if (mailingSameAsPhysical) {
        data.mailingAddress = data.physicalAddress;
        data.mailingCity = data.physicalCity;
        data.mailingState = data.physicalState;
        data.mailingZip = data.physicalZip;
      }
      await updateOrg(org.Rid, data);
      notifications.show({ title: 'Organization updated', message: data.orgName || 'Saved', color: 'green' });
      setEditOrgModalOpen(false);
    } catch (e: unknown) {
      notifications.show({ title: 'Error', message: e instanceof Error ? e.message : 'Failed to save organization', color: 'red' });
    } finally {
      setOrgSaving(false);
    }
  }

  // ── Delete handlers ──
  async function handleDeleteProject() {
    if (!deleteProjectTarget) return;
    setDeleting(true);
    try {
      await deleteProject(deleteProjectTarget.Rid);
      notifications.show({ title: 'Deleted', message: `Project "${deleteProjectTarget.projectTitle || deleteProjectTarget.projectNumber}" deleted`, color: 'orange' });
      setDeleteProjectTarget(null);
    } catch (e: unknown) {
      notifications.show({ title: 'Error', message: e instanceof Error ? e.message : 'Failed to delete', color: 'red' });
    } finally {
      setDeleting(false);
    }
  }

  async function handleDeleteDamage() {
    if (!deleteDamageTarget) return;
    setDeleting(true);
    try {
      await deleteDamageItem(deleteDamageTarget.Rid);
      notifications.show({ title: 'Deleted', message: `Damage site "${deleteDamageTarget.facilityName}" deleted`, color: 'orange' });
      setDeleteDamageTarget(null);
    } catch (e: unknown) {
      notifications.show({ title: 'Error', message: e instanceof Error ? e.message : 'Failed to delete', color: 'red' });
    } finally {
      setDeleting(false);
    }
  }

  // ── Document upload handler ──
  async function handleFileUpload(file: File) {
    setUploading(true);
    try {
      const FDoc = FIELDS.paDocuments;
      const today = new Date().toISOString().split('T')[0];
      const docType = deriveDocType(file.name);

      // Step 1: Create document record with metadata
      const fields: Array<[string, string]> = [
        [FDoc.organizationId, String(orgId)],
        [FDoc.documentName, file.name],
        [FDoc.documentType, docType],
        [FDoc.uploadDate, today],
        [FDoc.mimeType, file.type || 'application/octet-stream'],
        [FDoc.fileSize, String(file.size)],
      ];
      const rid = await createRecord(TABLES.paDocuments, fields, token ?? undefined);

      // Step 2: Upload the actual file via PUT with Base64EncodedFile
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Strip the data URL prefix (e.g., "data:application/pdf;base64,")
          const base64Data = result.split(',')[1] || result;
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // PUT the file data to the fileId field
      await updateRecord(TABLES.paDocuments, rid, [[FDoc.fileId, base64]], token ?? undefined);

      notifications.show({ title: 'Uploaded', message: file.name, color: 'green' });

      // Refetch documents (store fetchAll will reload everything)
      await fetchAll();
    } catch (e: unknown) {
      notifications.show({ title: 'Upload failed', message: e instanceof Error ? e.message : 'Error', color: 'red' });
    } finally {
      setUploading(false);
    }
  }

  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  }

  // ── Project form updater ──
  const pf = (field: keyof PAProject) => (val: string | null) =>
    setProjectForm(prev => ({ ...prev, [field]: val ?? '' }));

  // ── Damage form updater ──
  const df = (field: keyof PADamageItem) => (val: string | null) =>
    setDamageForm(prev => ({ ...prev, [field]: val ?? '' }));

  // ── Org form updater ──
  const of_ = (field: keyof PAOrganization) => (val: string | null) =>
    setOrgForm(prev => ({ ...prev, [field]: val ?? '' }));

  // ── Task handlers ──────────────────────────────────────────

  function openAddTask() {
    setTaskForm({ description: '', dueDate: '', projectId: '', status: 'pending' });
    setTaskModalOpen(true);
  }

  async function handleSaveTask() {
    if (!taskForm.description.trim()) {
      notifications.show({ message: 'Description is required', color: 'yellow' });
      return;
    }
    setTaskSaving(true);
    try {
      await createTimelineEvent({
        organizationId: String(orgId),
        projectId: taskForm.projectId || '',
        eventType: 'task',
        description: taskForm.description,
        dueDate: taskForm.dueDate,
        eventDate: new Date().toISOString().split('T')[0],
        status: taskForm.status,
      });
      notifications.show({ message: 'Task created', color: 'green' });
      setTaskModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setTaskSaving(false);
    }
  }

  async function handleToggleTask(t: { Rid: number; status: string; completedDate: string }) {
    const isComplete = t.status === 'completed';
    try {
      await updateTimelineEvent(t.Rid, {
        status: isComplete ? 'pending' : 'completed',
        completedDate: isComplete ? '' : new Date().toISOString().split('T')[0],
      });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Update failed', color: 'red' });
    }
  }

  async function handleDeleteTask(rid: number) {
    if (!confirm('Delete this task?')) return;
    try {
      await deleteTimelineEvent(rid);
      notifications.show({ message: 'Task deleted', color: 'gray' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    }
  }

  // ── Loading / not found ──────────────────────────────────
  const loading = loadingOrgs || loadingProjects || loadingDamage || loadingDocs;

  if (loading && !org) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!org) {
    return (
      <Center h={400}>
        <Stack align="center" gap="sm">
          <Text size="lg" c="dimmed">Organization not found</Text>
          <Button variant="light" leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(`${routeBase}/applicants`)}>
            Back to Applicants
          </Button>
        </Stack>
      </Center>
    );
  }

  // Org ownership guard: applicant roles can only view their own org
  if (isApplicantRole && myOrgIds.length > 0 && !myOrgIds.includes(String(org.Rid))) {
    return (
      <Center h={400}>
        <Stack align="center" gap="sm">
          <Text size="lg" c="dimmed">Access Denied</Text>
          <Text size="sm" c="dimmed">You do not have access to this organization.</Text>
          <Button variant="light" leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(routeBase)}>
            Back to Dashboard
          </Button>
        </Stack>
      </Center>
    );
  }

  // Project options for damage site linking
  const projectOptions = orgProjects.map(p => ({
    value: String(p.Rid),
    label: `${p.projectNumber || '---'} — ${p.projectTitle || 'Untitled'}`,
  }));

  // ── Render ───────────────────────────────────────────────
  return (
    <Stack gap="md">
      <Breadcrumbs items={[
        { label: 'Disaster Grants', to: '/pagrants' },
        { label: routeBase.includes('hmgp') ? 'HMGP' : 'PA', to: routeBase },
        { label: 'Applicants', to: `${routeBase}/applicants` },
        { label: org?.orgName || 'Applicant' },
      ]} />

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, alignItems: 'start' }}>
        {/* ═══ LEFT SIDEBAR ═══ */}
        <Paper p="md" withBorder style={{ position: 'sticky', top: 16 }}>
          <Group justify="space-between" mb="sm">
            <Title order={4}>{org.orgName}</Title>
            <ActionIcon variant="subtle" size="sm" onClick={openEditOrg}><IconEdit size={16} /></ActionIcon>
          </Group>

          <FieldRow label="Type" value={org.orgType} />
          <FieldRow label="Role" value={
            <Badge size="sm" color={ROLE_COLORS[org.orgRole] || 'gray'}>{org.orgRole}</Badge>
          } />
          <FieldRow label="FEMA ID" value={org.femaApplicantId} />
          <FieldRow label="UEI" value={org.ueiNumber} />
          <FieldRow label="FIPS" value={org.fipsCode} />
          <FieldRow label="Congressional District" value={org.congressionalDistrict} />

          {/* Primary Contact */}
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mt="md" mb={4}>Primary Contact</Text>
          <FieldRow label="Name" value={org.primaryContact} />
          <FieldRow label="Title" value={org.primaryTitle} />
          <FieldRow label="Email" value={
            org.primaryEmail
              ? <Text size="sm" fw={500} c="blue" component="a" href={`mailto:${org.primaryEmail}`}>{org.primaryEmail}</Text>
              : '---'
          } />
          <FieldRow label="Phone" value={org.primaryPhone} />

          {/* Address */}
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mt="md" mb={4}>Address</Text>
          <Stack gap={2} py={4}>
            {org.physicalAddress && <Text size="sm">{org.physicalAddress}</Text>}
            {(org.physicalCity || org.physicalState || org.physicalZip) && (
              <Text size="sm">
                {[org.physicalCity, org.physicalState].filter(Boolean).join(', ')}
                {org.physicalZip ? ` ${org.physicalZip}` : ''}
              </Text>
            )}
            {org.physicalCounty && <Text size="sm" c="dimmed">{org.physicalCounty} County</Text>}
          </Stack>

          {/* Readiness */}
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mt="md" mb={4}>Readiness</Text>
          <FieldRow label="SAM.gov" value={
            <Badge size="sm" color={org.samRegistered?.toLowerCase() === 'yes' ? 'green' : 'gray'}>
              {org.samRegistered?.toLowerCase() === 'yes' ? 'Registered' : 'Not Registered'}
            </Badge>
          } />
          <FieldRow label="Single Audit" value={
            <Badge size="sm" color={org.singleAuditCurrent?.toLowerCase() === 'yes' ? 'green' : 'gray'}>
              {org.singleAuditCurrent?.toLowerCase() === 'yes' ? 'Current' : 'N/A'}
            </Badge>
          } />
          <FieldRow label="Procurement" value={
            <Badge size="sm" color={org.procurementPolicyOnFile?.toLowerCase() === 'yes' ? 'green' : 'gray'}>
              {org.procurementPolicyOnFile?.toLowerCase() === 'yes' ? 'On File' : 'Missing'}
            </Badge>
          } />
          <FieldRow label="Force Acct." value={
            <Badge size="sm" color={org.forceAccountPolicyOnFile?.toLowerCase() === 'yes' ? 'green' : 'gray'}>
              {org.forceAccountPolicyOnFile?.toLowerCase() === 'yes' ? 'On File' : 'Missing'}
            </Badge>
          } />
        </Paper>

        {/* ═══ MAIN CONTENT ═══ */}
        <Stack gap="md">
          {/* Stats row */}
          <SimpleGrid cols={4}>
            <Paper p="md" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Projects</Text>
              <Text size="xl" fw={700}>{projectCount}</Text>
            </Paper>
            <Paper p="md" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Obligated</Text>
              <Text size="xl" fw={700}>{fmt(obligatedTotal)}</Text>
            </Paper>
            <Paper p="md" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Estimated Total</Text>
              <Text size="xl" fw={700}>{fmt(estimatedTotal)}</Text>
            </Paper>
            <Paper p="md" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Pending Review</Text>
              <Text size="xl" fw={700}>{pendingReview}</Text>
            </Paper>
          </SimpleGrid>

          {/* Tabs */}
          <Tabs defaultValue="projects">
            <Tabs.List>
              <Tabs.Tab value="projects">Projects &amp; Sites</Tabs.Tab>
              <Tabs.Tab value="documents">Documents ({orgDocuments.length})</Tabs.Tab>
              <Tabs.Tab value="tasks">Tasks ({orgTasks.length})</Tabs.Tab>
            </Tabs.List>

            {/* ── TAB 1: Projects & Sites ───────────────── */}
            <Tabs.Panel value="projects" pt="md">
              <Stack gap="lg">
                {/* Damage Sites */}
                <Paper p="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Title order={5}>Damage Sites</Title>
                    <Group gap="xs">
                      <Button size="xs" variant="light" leftSection={<IconSearch size={14} />}>
                        Import from Impact
                      </Button>
                      <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreateDamage}>
                        Add Site
                      </Button>
                    </Group>
                  </Group>
                  {orgDamageItems.length === 0 ? (
                    <Text c="dimmed" ta="center" py="lg">No damage sites recorded for this organization.</Text>
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Facility</Table.Th>
                          <Table.Th>Category</Table.Th>
                          <Table.Th>Severity</Table.Th>
                          <Table.Th>Est. Cost</Table.Th>
                          <Table.Th>Project</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th w={80}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {orgDamageItems.map(di => {
                          const cat = di.category as DamageCategory;
                          const linkedProject = orgProjects.find(p => String(p.Rid) === di.projectId);
                          return (
                            <Table.Tr key={di.Rid} style={{ cursor: 'pointer' }} onClick={() => openEditDamage(di)}>
                              <Table.Td>{di.facilityName || '---'}</Table.Td>
                              <Table.Td>
                                <Badge size="sm" style={{
                                  backgroundColor: DAMAGE_CATEGORY_COLORS[cat] || '#6B7280',
                                  color: '#fff',
                                }}>
                                  {DAMAGE_CATEGORY_LABELS[cat] || di.category || '---'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>{di.damageSeverity || '---'}</Table.Td>
                              <Table.Td>{di.estimatedCost ? fmt(parseFloat(di.estimatedCost) || 0) : '---'}</Table.Td>
                              <Table.Td>{linkedProject ? linkedProject.projectNumber || linkedProject.projectTitle : '---'}</Table.Td>
                              <Table.Td>
                                <Badge size="sm" color={PROJECT_STATUS_COLORS[di.status] || 'gray'}>
                                  {di.status}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Group gap={4}>
                                  <ActionIcon variant="subtle" size="sm" onClick={(e) => { e.stopPropagation(); openEditDamage(di); }}>
                                    <IconEdit size={14} />
                                  </ActionIcon>
                                  <ActionIcon variant="subtle" size="sm" color="red" onClick={(e) => { e.stopPropagation(); setDeleteDamageTarget(di); }}>
                                    <IconTrash size={14} />
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  )}
                </Paper>

                {/* Projects */}
                <Paper p="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Title order={5}>Projects</Title>
                    <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreateProject}>
                      New Project
                    </Button>
                  </Group>
                  {orgProjects.length === 0 ? (
                    <Text c="dimmed" ta="center" py="lg">No projects yet for this organization.</Text>
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Project #</Table.Th>
                          <Table.Th>Title</Table.Th>
                          <Table.Th>Cat</Table.Th>
                          <Table.Th>Size</Table.Th>
                          <Table.Th>Phase</Table.Th>
                          <Table.Th>Est. Cost</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th w={80}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {orgProjects.map(p => {
                          const cat = p.category as DamageCategory;
                          const wf = projectWorkflows.get(String(p.Rid));
                          return (
                            <Table.Tr key={p.Rid} style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`${routeBase}/applicants/${orgId}/projects/${p.Rid}`)}>
                              <Table.Td>{p.projectNumber || '---'}</Table.Td>
                              <Table.Td>{p.projectTitle || '---'}</Table.Td>
                              <Table.Td>
                                <Badge size="sm" style={{
                                  backgroundColor: DAMAGE_CATEGORY_COLORS[cat] || '#6B7280',
                                  color: '#fff',
                                }}>
                                  {cat || '---'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Badge size="sm" color={p.projectSize === 'large' ? 'blue' : 'gray'}>
                                  {p.projectSize || '---'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Badge size="sm" color={PHASE_COLORS[p.phase] || 'gray'}>
                                  {p.phase}
                                </Badge>
                              </Table.Td>
                              <Table.Td>{p.estimatedCost ? fmt(parseFloat(p.estimatedCost) || 0) : '---'}</Table.Td>
                              <Table.Td>
                                <Badge size="sm" color={PROJECT_STATUS_COLORS[p.status] || 'gray'}>
                                  {p.status}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Group gap={4} wrap="nowrap">
                                  {wf && (
                                    <Badge size="xs" variant="dot" color="violet" title={`Workflow: Step ${parseInt(wf.instance.currentStepIndex) + 1}/${wf.instance.totalSteps}`}>
                                      <IconGitMerge size={10} />
                                    </Badge>
                                  )}
                                  <ActionIcon variant="subtle" size="sm" color="blue" title="Open project workspace"
                                    onClick={(e) => { e.stopPropagation(); navigate(`${routeBase}/applicants/${orgId}/projects/${p.Rid}`); }}>
                                    <IconExternalLink size={14} />
                                  </ActionIcon>
                                  <ActionIcon variant="subtle" size="sm" onClick={(e) => { e.stopPropagation(); openEditProject(p); }}>
                                    <IconEdit size={14} />
                                  </ActionIcon>
                                  <ActionIcon variant="subtle" size="sm" color="red" onClick={(e) => { e.stopPropagation(); setDeleteProjectTarget(p); }}>
                                    <IconTrash size={14} />
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  )}
                </Paper>

                {/* ── Active Workflow Panels (inline per project) ──── */}
                {orgProjects.filter(p => projectWorkflows.has(String(p.Rid))).map(p => {
                  const wf = projectWorkflows.get(String(p.Rid))!;
                  const { instance, activeStep, stepDefs, allSteps } = wf;
                  const currentIdx = parseInt(instance.currentStepIndex) || 0;
                  const total = parseInt(instance.totalSteps) || stepDefs.length || 1;
                  const pct = Math.round(((currentIdx) / total) * 100);
                  const currentDef = stepDefs[currentIdx];

                  return (
                    <Paper key={`wf-${p.Rid}`} p="md" withBorder
                      style={{ borderLeft: '3px solid var(--mantine-color-violet-5)' }}>
                      <Group justify="space-between" mb="xs">
                        <Group gap="xs">
                          <ThemeIcon size="sm" variant="light" color="violet">
                            <IconGitMerge size={14} />
                          </ThemeIcon>
                          <Text size="sm" fw={600}>{p.projectNumber} — {instance.templateName}</Text>
                        </Group>
                        <Badge size="sm" color="violet" variant="light">
                          Step {currentIdx + 1} of {total}
                        </Badge>
                      </Group>

                      <Progress value={pct} size="xs" color="violet" mb="sm" />

                      {/* Step timeline */}
                      <Group gap={4} mb="sm" style={{ flexWrap: 'wrap' }}>
                        {stepDefs.map((sd, si) => {
                          const step = allSteps.find(s => parseInt(s.stepIndex) === si);
                          const status = step?.status || 'pending';
                          const color = status === 'completed' ? 'green' : status === 'in_progress' ? 'blue' : 'gray';
                          return (
                            <Badge key={si} size="xs" variant={si === currentIdx ? 'filled' : 'light'} color={color}>
                              {si + 1}. {sd.name}
                            </Badge>
                          );
                        })}
                      </Group>

                      {/* Active step details */}
                      {currentDef && (
                        <Stack gap="xs">
                          <Group gap="xs">
                            <Text size="xs" c="dimmed">Current:</Text>
                            <Badge size="xs" variant="dot" style={{ '--badge-dot-color': STEP_TYPE_COLORS[currentDef.type] || '#64748B' } as React.CSSProperties}>
                              {STEP_TYPE_LABELS[currentDef.type]}
                            </Badge>
                            <Text size="sm" fw={500}>{currentDef.name}</Text>
                            {activeStep && (
                              <Text size="xs" c="dimmed">— assigned to {activeStep.assignee}</Text>
                            )}
                          </Group>

                          {/* Instructions */}
                          {currentDef.instructions && (
                            <Paper p="sm" radius="sm" style={{ backgroundColor: 'var(--mantine-color-violet-0)', border: '1px solid var(--mantine-color-violet-2)' }}>
                              <Group gap={6} mb={4}>
                                <IconInfoCircle size={14} color="var(--mantine-color-violet-6)" />
                                <Text size="xs" fw={600} c="violet.7">Instructions</Text>
                              </Group>
                              <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{currentDef.instructions}</Text>
                            </Paper>
                          )}

                          {/* Document checklist */}
                          {currentDef.documentChecklist && currentDef.documentChecklist.length > 0 && (
                            <Paper p="sm" radius="sm" withBorder>
                              <Group gap={6} mb="xs">
                                <IconListCheck size={14} color="var(--mantine-color-blue-6)" />
                                <Text size="xs" fw={600} c="blue.7">Required Documents</Text>
                                <Badge size="xs" variant="light" color="blue">
                                  {currentDef.documentChecklist.filter(d => d.required).length} required
                                </Badge>
                              </Group>
                              <Stack gap={4}>
                                {currentDef.documentChecklist.map((doc, di) => (
                                  <Group key={di} gap="xs">
                                    <IconCircle size={10} color={doc.required ? 'var(--mantine-color-red-5)' : 'var(--mantine-color-gray-4)'} />
                                    <Text size="xs">{doc.label}</Text>
                                    {doc.required && <Badge size="xs" variant="outline" color="red">Required</Badge>}
                                    {doc.description && <Text size="xs" c="dimmed">— {doc.description}</Text>}
                                  </Group>
                                ))}
                              </Stack>
                            </Paper>
                          )}
                        </Stack>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </Tabs.Panel>

            {/* ── TAB 2: Documents ──────────────────────── */}
            <Tabs.Panel value="documents" pt="md">
              <Stack gap="md">
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.pptx,.ppt,.png,.jpg,.jpeg,.gif,.zip,.rar"
                  onChange={onFileInputChange}
                />

                {/* Upload zone */}
                <Paper p="xl" withBorder style={{
                  border: '2px dashed var(--mantine-color-gray-4)',
                  textAlign: 'center',
                  cursor: uploading ? 'wait' : 'pointer',
                  borderRadius: 8,
                  opacity: uploading ? 0.6 : 1,
                }} onClick={() => !uploading && fileInputRef.current?.click()}>
                  <Stack align="center" gap="xs">
                    {uploading ? <Loader size={32} /> : <IconUpload size={32} color="var(--mantine-color-gray-5)" />}
                    <Text size="sm" c="dimmed">
                      {uploading ? 'Uploading...' : 'Drag & drop files here or click to browse'}
                    </Text>
                    <Text size="xs" c="dimmed">PDF, DOCX, XLSX, images, ZIP accepted</Text>
                  </Stack>
                </Paper>

                {/* Grouped file list */}
                {orgDocuments.length === 0 ? (
                  <Text c="dimmed" ta="center" py="lg">No documents uploaded for this organization.</Text>
                ) : (
                  Object.entries(docsByType).map(([type, docs]) => (
                    <Paper key={type} p="md" withBorder>
                      <Text size="sm" fw={600} mb="xs" tt="uppercase" c="dimmed">{type}</Text>
                      <Stack gap={4}>
                        {docs.map(doc => (
                          <Group key={doc.Rid} justify="space-between" py={6}
                            style={{ borderBottom: '1px solid var(--mantine-color-gray-1)' }}>
                            <Group gap="sm">
                              {fileIcon(doc.mimeType || '')}
                              <div>
                                <Text size="sm" fw={500}>{doc.documentName || 'Untitled'}</Text>
                                <Text size="xs" c="dimmed">
                                  {[
                                    doc.fileSize ? `${doc.fileSize}` : null,
                                    doc.uploadDate || null,
                                    doc.uploadedBy || null,
                                  ].filter(Boolean).join(' \u00B7 ')}
                                </Text>
                              </div>
                            </Group>
                            <Group gap="xs">
                              <Badge size="sm" variant="light">{doc.documentType || 'Other'}</Badge>
                              <ActionIcon variant="subtle" size="sm"><IconDownload size={16} /></ActionIcon>
                              <ActionIcon variant="subtle" size="sm" color="red"><IconTrash size={16} /></ActionIcon>
                            </Group>
                          </Group>
                        ))}
                      </Stack>
                    </Paper>
                  ))
                )}
              </Stack>
            </Tabs.Panel>

            {/* ── TAB 3: Tasks ──────────────────────────── */}
            <Tabs.Panel value="tasks" pt="md">
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={600}>Tasks &amp; Deadlines</Text>
                  <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddTask}>
                    Add Task
                  </Button>
                </Group>

                {orgTasks.length === 0 ? (
                  <Paper p="xl" withBorder>
                    <Stack align="center" gap="sm">
                      <Text c="dimmed">No tasks yet. Click Add Task to create one.</Text>
                    </Stack>
                  </Paper>
                ) : (
                  <Stack gap="xs">
                    {orgTasks.map(t => {
                      const overdue = t.dueDate && t.dueDate < today && t.status !== 'completed';
                      const proj = t.projectId ? orgProjects.find(p => String(p.Rid) === t.projectId) : null;
                      return (
                        <Paper key={t.Rid} p="sm" withBorder
                          style={{ borderLeft: `3px solid ${overdue ? '#DC2626' : t.status === 'completed' ? '#059669' : '#6B7280'}` }}
                        >
                          <Group justify="space-between" wrap="nowrap">
                            <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                              <ActionIcon
                                variant="subtle"
                                color={t.status === 'completed' ? 'green' : 'gray'}
                                onClick={() => handleToggleTask(t)}
                              >
                                {t.status === 'completed' ? <IconCircleCheck size={20} /> : <IconCircle size={20} />}
                              </ActionIcon>
                              <Stack gap={2} style={{ flex: 1 }}>
                                <Text size="sm" fw={500}
                                  td={t.status === 'completed' ? 'line-through' : undefined}
                                  c={t.status === 'completed' ? 'dimmed' : undefined}
                                >
                                  {t.description}
                                </Text>
                                <Group gap="xs">
                                  {t.dueDate && (
                                    <Badge size="xs" variant="light"
                                      color={overdue ? 'red' : 'gray'}
                                      leftSection={<IconClock size={10} />}
                                    >
                                      Due {t.dueDate}
                                    </Badge>
                                  )}
                                  {proj && (
                                    <Badge size="xs" variant="light" color="blue">
                                      {proj.projectNumber || `Project #${proj.Rid}`}
                                    </Badge>
                                  )}
                                  <Badge size="xs" variant="light"
                                    color={t.status === 'completed' ? 'green' : t.status === 'in_progress' ? 'yellow' : 'gray'}
                                  >
                                    {t.status}
                                  </Badge>
                                </Group>
                              </Stack>
                            </Group>
                            <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleDeleteTask(t.Rid)}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Group>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </div>

      {/* ════════════════════════════════════════════════════════════ */}
      {/* MODALS                                                      */}
      {/* ════════════════════════════════════════════════════════════ */}

      {/* ── Create/Edit Project Modal ──────────────────────────── */}
      <Modal opened={projectModalOpen} onClose={() => setProjectModalOpen(false)}
        title={editingProject ? 'Edit Project' : 'New Project'} size="xl"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}>
        <Tabs value={projectTab} onChange={setProjectTab}>
          <Tabs.List>
            <Tabs.Tab value="general">General</Tabs.Tab>
            <Tabs.Tab value="location">Location</Tabs.Tab>
            <Tabs.Tab value="scope">Scope</Tabs.Tab>
            <Tabs.Tab value="cost">Cost</Tabs.Tab>
            <Tabs.Tab value="status">Status</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="general" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <TextInput label="Project Number" readOnly
                  description="Auto-assigned"
                  value={projectForm.projectNumber || ''}
                  styles={{ input: { fontFamily: 'DM Mono, monospace', fontWeight: 600 } }} />
                <TextInput label="Project Title" required value={projectForm.projectTitle || ''}
                  onChange={e => pf('projectTitle')(e.currentTarget.value)} />
              </SimpleGrid>
              <SimpleGrid cols={3}>
                <Select label="Category" data={CATEGORY_OPTIONS} value={projectForm.category || null}
                  onChange={pf('category')} clearable />
                <Select label="Project Size" data={PROJECT_SIZE_OPTIONS} value={projectForm.projectSize || null}
                  onChange={pf('projectSize')} clearable />
                <Select label="Work Type" data={WORK_TYPE_OPTIONS} value={projectForm.workType || null}
                  onChange={pf('workType')} clearable searchable />
              </SimpleGrid>
              <TextInput label="Assigned Specialist" value={projectForm.assignedSpecialist || ''}
                onChange={e => pf('assignedSpecialist')(e.currentTarget.value)} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="location" pt="md">
            <Stack gap="sm">
              <Popover opened={addrOpen} onChange={setAddrOpen} position="bottom-start" width="target">
                <Popover.Target>
                  <TextInput label="Address" placeholder="Start typing to search..."
                    description="Type 3+ characters to autocomplete — fills city, county, state, ZIP"
                    value={projectForm.projectAddress || ''}
                    onChange={e => {
                      pf('projectAddress')(e.currentTarget.value);
                      fetchAddrSuggestions(e.currentTarget.value);
                    }}
                    onFocus={() => addrSuggestions.length > 0 && setAddrOpen(true)}
                  />
                </Popover.Target>
                <Popover.Dropdown p={0}>
                  {addrSuggestions.map((s, i) => (
                    <div key={i}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                      onMouseDown={() => pickProjectAddress(s)}
                    >
                      <Text size="sm" fw={500}>{s.title}</Text>
                      {s.address?.label && s.address.label !== s.title && (
                        <Text size="xs" c="dimmed">{s.address.label}</Text>
                      )}
                    </div>
                  ))}
                </Popover.Dropdown>
              </Popover>
              <SimpleGrid cols={2}>
                <TextInput label="City" value={projectForm.projectCity || ''}
                  onChange={e => pf('projectCity')(e.currentTarget.value)} />
                <TextInput label="County" value={projectForm.projectCounty || ''}
                  onChange={e => pf('projectCounty')(e.currentTarget.value)} />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="State" value={projectForm.projectState || ''}
                  onChange={e => pf('projectState')(e.currentTarget.value)} />
                <TextInput label="ZIP" value={projectForm.projectZip || ''}
                  onChange={e => pf('projectZip')(e.currentTarget.value)} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="scope" pt="md">
            <Stack gap="sm">
              <Textarea label="Damage Description & Dimensions (DDD)"
                description="Describe all damage including facility name, pre-disaster condition, cause of damage, and dimensions"
                autosize minRows={8} maxRows={30}
                value={projectForm.damageDescription || ''}
                onChange={e => pf('damageDescription')(e.currentTarget.value)} />
              <Textarea label="Scope of Work (SOW)"
                description="Detailed scope including materials, methods, labor, equipment, and quantities"
                autosize minRows={8} maxRows={30}
                value={projectForm.scopeOfWork || ''}
                onChange={e => pf('scopeOfWork')(e.currentTarget.value)} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="cost" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <TextInput label="Estimated Cost" placeholder="$0"
                  value={formatCurrency(projectForm.estimatedCost || '')}
                  onChange={e => pf('estimatedCost')(stripCurrency(e.currentTarget.value))}
                  styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
                <TextInput label="Federal Share %"
                  value={projectForm.federalSharePct || '75'}
                  onChange={e => pf('federalSharePct')(e.currentTarget.value)}
                  styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="status" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <Select label="Phase" data={PROJECT_PHASE_OPTIONS} value={projectForm.phase || null}
                  onChange={pf('phase')} />
                <Select label="Status" data={PROJECT_STATUS_OPTIONS} value={projectForm.status || null}
                  onChange={pf('status')} />
              </SimpleGrid>
              <Textarea label="Notes" autosize minRows={3} maxRows={10} value={projectForm.notes || ''}
                onChange={e => pf('notes')(e.currentTarget.value)} />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setProjectModalOpen(false)}>Cancel</Button>
          <Button loading={projectSaving} onClick={handleSaveProject}>
            {editingProject ? 'Update Project' : 'Create Project'}
          </Button>
        </Group>
      </Modal>

      {/* ── Create/Edit Damage Site Modal ──────────────────────── */}
      <Modal opened={damageModalOpen} onClose={() => setDamageModalOpen(false)}
        title={editingDamage ? 'Edit Damage Site' : 'New Damage Site'} size="lg">
        <Stack gap="sm">
          <SimpleGrid cols={2}>
            <TextInput label="Facility Name" value={damageForm.facilityName || ''}
              onChange={e => df('facilityName')(e.currentTarget.value)} />
            <TextInput label="Facility Type" value={damageForm.facilityType || ''}
              onChange={e => df('facilityType')(e.currentTarget.value)} />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <Select label="Category" data={CATEGORY_OPTIONS} value={damageForm.category || null}
              onChange={df('category')} clearable />
            <Select label="Damage Severity" data={DAMAGE_SEVERITY_OPTIONS} value={damageForm.damageSeverity || null}
              onChange={df('damageSeverity')} clearable />
          </SimpleGrid>

          <TextInput label="Address" value={damageForm.siteAddress || ''}
            onChange={e => df('siteAddress')(e.currentTarget.value)} />
          <SimpleGrid cols={2}>
            <TextInput label="City" value={damageForm.siteCity || ''}
              onChange={e => df('siteCity')(e.currentTarget.value)} />
            <TextInput label="County" value={damageForm.siteCounty || ''}
              onChange={e => df('siteCounty')(e.currentTarget.value)} />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <TextInput label="State" value={damageForm.siteState || ''}
              onChange={e => df('siteState')(e.currentTarget.value)} />
            <TextInput label="ZIP" value={damageForm.siteZip || ''}
              onChange={e => df('siteZip')(e.currentTarget.value)} />
          </SimpleGrid>

          <Textarea label="Damage Description" minRows={3} value={damageForm.damageDescription || ''}
            onChange={e => df('damageDescription')(e.currentTarget.value)} />
          <SimpleGrid cols={2}>
            <TextInput label="Damage Cause" value={damageForm.damageCause || ''}
              onChange={e => df('damageCause')(e.currentTarget.value)} />
            <TextInput label="Estimated Cost" value={damageForm.estimatedCost || ''}
              onChange={e => df('estimatedCost')(e.currentTarget.value)} />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <TextInput label="Pre-Disaster Condition" value={damageForm.preDisasterCondition || ''}
              onChange={e => df('preDisasterCondition')(e.currentTarget.value)} />
            <TextInput label="Pre-Disaster Use" value={damageForm.preDisasterUse || ''}
              onChange={e => df('preDisasterUse')(e.currentTarget.value)} />
          </SimpleGrid>
          <SimpleGrid cols={3}>
            <Select label="Site Inspected" data={YES_NO_OPTIONS} value={damageForm.siteInspected || null}
              onChange={df('siteInspected')} clearable />
            <TextInput label="Inspection Date" type="date" value={damageForm.siteInspectionDate || ''}
              onChange={e => df('siteInspectionDate')(e.currentTarget.value)} />
            <Select label="Link to Project" data={projectOptions} value={damageForm.projectId || null}
              onChange={df('projectId')} clearable searchable />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <Select label="Status" data={DAMAGE_STATUS_OPTIONS} value={damageForm.status || null}
              onChange={df('status')} />
            <div />
          </SimpleGrid>
          <Textarea label="Notes" minRows={2} value={damageForm.notes || ''}
            onChange={e => df('notes')(e.currentTarget.value)} />
        </Stack>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setDamageModalOpen(false)}>Cancel</Button>
          <Button loading={damageSaving} onClick={handleSaveDamage}>
            {editingDamage ? 'Update Site' : 'Create Site'}
          </Button>
        </Group>
      </Modal>

      {/* ── Edit Organization Modal ────────────────────────────── */}
      <Modal opened={editOrgModalOpen} onClose={() => setEditOrgModalOpen(false)}
        title="Edit Organization" size="lg">
        <Tabs value={orgTab} onChange={setOrgTab}>
          <Tabs.List>
            <Tabs.Tab value="basic">Basic</Tabs.Tab>
            <Tabs.Tab value="address">Address</Tabs.Tab>
            <Tabs.Tab value="contacts">Contacts</Tabs.Tab>
            <Tabs.Tab value="financial">Financial</Tabs.Tab>
            <Tabs.Tab value="notes">Notes</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="basic" pt="md">
            <Stack gap="sm">
              <TextInput label="Organization Name" value={orgForm.orgName || ''}
                onChange={e => of_('orgName')(e.currentTarget.value)} />
              <SimpleGrid cols={2}>
                <Select label="Type" data={ORG_TYPE_OPTIONS} value={orgForm.orgType || null}
                  onChange={of_('orgType')} clearable />
                <Select label="Role" data={ORG_ROLE_OPTIONS} value={orgForm.orgRole || null}
                  onChange={of_('orgRole')} clearable />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="FIPS Code" value={orgForm.fipsCode || ''}
                  onChange={e => of_('fipsCode')(e.currentTarget.value)} />
                <TextInput label="FEMA Applicant ID" value={orgForm.femaApplicantId || ''}
                  onChange={e => of_('femaApplicantId')(e.currentTarget.value)} />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="UEI Number" value={orgForm.ueiNumber || ''}
                  onChange={e => of_('ueiNumber')(e.currentTarget.value)} />
                <TextInput label="Congressional District" value={orgForm.congressionalDistrict || ''}
                  onChange={e => of_('congressionalDistrict')(e.currentTarget.value)} />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <Select label="SAM.gov Registered" data={YES_NO_OPTIONS} value={orgForm.samRegistered || null}
                  onChange={of_('samRegistered')} clearable />
                <TextInput label="SAM Expiration Date" type="date" value={orgForm.samExpirationDate || ''}
                  onChange={e => of_('samExpirationDate')(e.currentTarget.value)} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="address" pt="md">
            <Stack gap="sm">
              <Text size="sm" fw={600}>Physical Address</Text>
              <TextInput label="Street" value={orgForm.physicalAddress || ''}
                onChange={e => of_('physicalAddress')(e.currentTarget.value)} />
              <SimpleGrid cols={2}>
                <TextInput label="City" value={orgForm.physicalCity || ''}
                  onChange={e => of_('physicalCity')(e.currentTarget.value)} />
                <TextInput label="County" value={orgForm.physicalCounty || ''}
                  onChange={e => of_('physicalCounty')(e.currentTarget.value)} />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="State" value={orgForm.physicalState || ''}
                  onChange={e => of_('physicalState')(e.currentTarget.value)} />
                <TextInput label="ZIP" value={orgForm.physicalZip || ''}
                  onChange={e => of_('physicalZip')(e.currentTarget.value)} />
              </SimpleGrid>

              <Checkbox mt="md" label="Mailing address same as physical"
                checked={mailingSameAsPhysical}
                onChange={e => setMailingSameAsPhysical(e.currentTarget.checked)} />

              {!mailingSameAsPhysical && (
                <>
                  <Text size="sm" fw={600} mt="sm">Mailing Address</Text>
                  <TextInput label="Street" value={orgForm.mailingAddress || ''}
                    onChange={e => of_('mailingAddress')(e.currentTarget.value)} />
                  <SimpleGrid cols={2}>
                    <TextInput label="City" value={orgForm.mailingCity || ''}
                      onChange={e => of_('mailingCity')(e.currentTarget.value)} />
                    <TextInput label="State" value={orgForm.mailingState || ''}
                      onChange={e => of_('mailingState')(e.currentTarget.value)} />
                  </SimpleGrid>
                  <TextInput label="ZIP" value={orgForm.mailingZip || ''}
                    onChange={e => of_('mailingZip')(e.currentTarget.value)} />
                </>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="contacts" pt="md">
            <Stack gap="sm">
              <Text size="sm" fw={600}>Primary Contact</Text>
              <SimpleGrid cols={2}>
                <TextInput label="Name" value={orgForm.primaryContact || ''}
                  onChange={e => of_('primaryContact')(e.currentTarget.value)} />
                <TextInput label="Title" value={orgForm.primaryTitle || ''}
                  onChange={e => of_('primaryTitle')(e.currentTarget.value)} />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="Email" value={orgForm.primaryEmail || ''}
                  onChange={e => of_('primaryEmail')(e.currentTarget.value)} />
                <TextInput label="Phone" value={orgForm.primaryPhone || ''}
                  onChange={e => of_('primaryPhone')(e.currentTarget.value)} />
              </SimpleGrid>

              <Text size="sm" fw={600} mt="md">Alternate Contact</Text>
              <SimpleGrid cols={2}>
                <TextInput label="Name" value={orgForm.altContact || ''}
                  onChange={e => of_('altContact')(e.currentTarget.value)} />
                <TextInput label="Title" value={orgForm.altTitle || ''}
                  onChange={e => of_('altTitle')(e.currentTarget.value)} />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="Email" value={orgForm.altEmail || ''}
                  onChange={e => of_('altEmail')(e.currentTarget.value)} />
                <TextInput label="Phone" value={orgForm.altPhone || ''}
                  onChange={e => of_('altPhone')(e.currentTarget.value)} />
              </SimpleGrid>

              <Textarea label="Authorized Portal Users" mt="sm"
                description="Comma-separated emails of users who can access this org's PA Grants portal"
                placeholder="user1@example.com, user2@example.com"
                value={orgForm.contactEmails || ''}
                onChange={e => of_('contactEmails')(e.currentTarget.value)}
                rows={2} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="financial" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <Select label="Procurement Policy on File" data={YES_NO_OPTIONS}
                  value={orgForm.procurementPolicyOnFile || null}
                  onChange={of_('procurementPolicyOnFile')} clearable />
                <Select label="Force Account Policy on File" data={YES_NO_OPTIONS}
                  value={orgForm.forceAccountPolicyOnFile || null}
                  onChange={of_('forceAccountPolicyOnFile')} clearable />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <Select label="Single Audit Required" data={YES_NO_OPTIONS}
                  value={orgForm.singleAuditRequired || null}
                  onChange={of_('singleAuditRequired')} clearable />
                <Select label="Single Audit Current" data={YES_NO_OPTIONS}
                  value={orgForm.singleAuditCurrent || null}
                  onChange={of_('singleAuditCurrent')} clearable />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="Indirect Cost Rate" value={orgForm.indirectCostRate || ''}
                  onChange={e => of_('indirectCostRate')(e.currentTarget.value)} />
                <Select label="Indirect Cost Rate Type" data={INDIRECT_COST_RATE_TYPE_OPTIONS}
                  value={orgForm.indirectCostRateType || null}
                  onChange={of_('indirectCostRateType')} clearable />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="notes" pt="md">
            <Stack gap="sm">
              <Select label="Status" data={ORG_STATUS_OPTIONS} value={orgForm.status || null}
                onChange={of_('status')} />
              <Textarea label="Notes" minRows={4} value={orgForm.notes || ''}
                onChange={e => of_('notes')(e.currentTarget.value)} />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setEditOrgModalOpen(false)}>Cancel</Button>
          <Button loading={orgSaving} onClick={handleSaveOrg}>Update Organization</Button>
        </Group>
      </Modal>

      {/* ── Delete Project Confirmation ────────────────────────── */}
      <Modal opened={!!deleteProjectTarget} onClose={() => setDeleteProjectTarget(null)}
        title="Delete Project" size="sm">
        <Text size="sm">
          Delete <strong>{deleteProjectTarget?.projectTitle || deleteProjectTarget?.projectNumber || 'this project'}</strong>? This cannot be undone.
        </Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setDeleteProjectTarget(null)}>Cancel</Button>
          <Button color="red" loading={deleting} onClick={handleDeleteProject}>Delete</Button>
        </Group>
      </Modal>

      {/* ── Delete Damage Site Confirmation ─────────────────────── */}
      <Modal opened={!!deleteDamageTarget} onClose={() => setDeleteDamageTarget(null)}
        title="Delete Damage Site" size="sm">
        <Text size="sm">
          Delete <strong>{deleteDamageTarget?.facilityName || 'this damage site'}</strong>? This cannot be undone.
        </Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setDeleteDamageTarget(null)}>Cancel</Button>
          <Button color="red" loading={deleting} onClick={handleDeleteDamage}>Delete</Button>
        </Group>
      </Modal>

      {/* ── Add Task Modal ─────────────────────────────────────── */}
      <Modal opened={taskModalOpen} onClose={() => setTaskModalOpen(false)} title="Add Task" size="md">
        <Stack gap="sm">
          <Textarea label="Description" required
            value={taskForm.description}
            onChange={e => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
          />
          <TextInput label="Due Date" type="date"
            value={taskForm.dueDate}
            onChange={e => setTaskForm(prev => ({ ...prev, dueDate: e.target.value }))}
          />
          <Select label="Project" placeholder="(optional)" clearable
            data={orgProjects.map(p => ({ value: String(p.Rid), label: p.projectNumber ? `${p.projectNumber} — ${p.projectTitle}` : p.projectTitle }))}
            value={taskForm.projectId || null}
            onChange={val => setTaskForm(prev => ({ ...prev, projectId: val || '' }))}
          />
          <Select label="Status"
            data={[
              { value: 'pending', label: 'Pending' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'completed', label: 'Complete' },
            ]}
            value={taskForm.status}
            onChange={val => setTaskForm(prev => ({ ...prev, status: val || 'pending' }))}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setTaskModalOpen(false)}>Cancel</Button>
            <Button loading={taskSaving} onClick={handleSaveTask}>Create Task</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
